/* eslint react/display-name: 0 */
import React from 'react'
import PropTypes from 'prop-types'
import { Provider, connect } from 'react-redux'
import R from 'ramda'
import {
  Dropdown,
  Menu,
  Container,
  Segment,
  Button
} from 'semantic-ui-react'

import UsersPage from '_/users'
import InstitutionsPage from '_/institutions'
import ResellersPage from '_/resellers'
import SerialsPage from '_/serials'
import LicensesPage from '_/licenses'
import FastspringPage from '_/fastspring'
import ThirdPartyInstitution from '_/thirdParty'
import APITokensPage from '_/apitokens'

import LoginForm from '_forms/login'

import {
  PAGES,
  COLORS
} from '_constants'

import {
  logout,
  goToTab,
  clearAuthError,
  loginAndGoToPage
} from '_actions/index'

import {
  isAuthAdmin,
  isInstAdmin,
  isLicenseAdmin,
  isLicenseViewer
} from './utils'

import store from './store'
import { UserProvider } from './contexts/authUser'

const helpOptions = [
  { key: 'authhelp', text: 'Help', value: 'help', icon: 'help circle' },
  { key: 'auth_options', text: 'Authorization Options', value: 'ac_ag', as: 'a', href: 'https://assets.cdn.cycling74.com/auth_guides/authorization_options.pdf', target: '_blank' },
  { key: 'admin_portal_guide', text: 'Admin Portal Guide', value: 'ac_ag', as: 'a', href: 'https://assets.cdn.cycling74.com/auth_guides/admin_portal_guide.pdf', target: '_blank' },
  { key: 'ac_admin_guide', text: 'Activation Code Guide', value: 'ac_ag', as: 'a', href: 'https://assets.cdn.cycling74.com/auth_guides/ac_admin_guide.pdf', target: '_blank' },
  { key: 'ac_activation_guide', text: 'Activating Your AC', value: 'ac_ug', as: 'a', href: 'https://assets.cdn.cycling74.com/auth_guides/ac_activation.pdf', target: '_blank' },
  { key: 'keyfile_admin_guide', text: 'KeyFile Guide', value: 'kf_ag', as: 'a', href: 'https://assets.cdn.cycling74.com/auth_guides/keyfile_admin_guide.pdf', target: '_blank' },
  { key: 'auth_options_ja', text: 'Authorization Options (Japanese)', value: 'ac_ag_ja', as: 'a', href: 'https://assets.cdn.cycling74.com/auth_guides/authorization_options_ja.pdf', target: '_blank' },
  { key: 'admin_portal_guide_ja', text: 'Admin Portal Guide (Japanese)', value: 'ac_ag_ja', as: 'a', href: 'https://assets.cdn.cycling74.com/auth_guides/admin_portal_guide_ja.pdf', target: '_blank' },
  { key: 'ac_admin_guide_ja', text: 'Activation Code Guide (Japanese)', value: 'ac_ag_ja', as: 'a', href: 'https://assets.cdn.cycling74.com/auth_guides/ac_admin_guide_ja.pdf', target: '_blank' },
  { key: 'ac_activation_guide_ja', text: 'Activating Your AC (Japanese)', value: 'ac_ug_ja', as: 'a', href: 'https://assets.cdn.cycling74.com/auth_guides/ac_activation_ja.pdf', target: '_blank' },
  { key: 'keyfile_admin_guide_ja', text: 'KeyFile Guide (Japanese)', value: 'kf_ag_ja', as: 'a', href: 'https://assets.cdn.cycling74.com/auth_guides/keyfile_admin_guide_ja.pdf', target: '_blank' },
];

const HelpMenuItem = () => (
  <Menu.Item>
    <Dropdown placeholder='Help' value='help' color='blue' options={helpOptions} icon='help circle' />
  </Menu.Item>
)

const _AuthMain = ({
  pageName,
  authdUser,
  // navigation,
  loginError,

  // functions/actions
  loginAndGoToPage,
  clearAuthError,
  goToTab,
  logout,
  postLoginNavigation = null,
}) => {
  const tabIt = pageName => _e => goToTab({pageName})

  if (R.isNil(authdUser)) {
    return <LoginForm
      clearAuthError={clearAuthError}
      loginAndGoToPage={loginAndGoToPage}
      navigation={postLoginNavigation}
      loginError={loginError}
    />
  }
  return isAuthAdmin(authdUser) || isLicenseAdmin(authdUser) || isLicenseViewer(authdUser) ? (
    <Container fluid>
      <Menu pointing borderless attached='top'>
        <Menu.Item color={COLORS.USERS.MENU} name='Users' active={pageName === PAGES.USERS} onClick={tabIt(PAGES.USERS)} />
        <Menu.Item color={COLORS.INSTITUTIONS.MENU} name='Institutions' active={pageName === PAGES.INSTITUTIONS} onClick={tabIt(PAGES.INSTITUTIONS)} />
        <Menu.Item color={COLORS.RESELLERS.MENU} name='Resellers' active={pageName === PAGES.RESELLERS} onClick={tabIt(PAGES.RESELLERS)} />
        <Menu.Item color={COLORS.SERIALS.MENU} name='Serials' active={pageName === PAGES.SERIALS} onClick={tabIt(PAGES.SERIALS)} />
        <Menu.Item color={COLORS.LICENSES.MENU} name='Licenses' active={pageName === PAGES.LICENSES} onClick={tabIt(PAGES.LICENSES)} />
        <Menu.Item color={COLORS.FASTSPRING.MENU} name='FS' active={pageName === PAGES.FASTSPRING} onClick={tabIt(PAGES.FASTSPRING)} />
        <Menu.Menu position='right'>
          {
            isAuthAdmin(authdUser) ? (
              <Menu.Item color={COLORS.API_TOKENS.MENU} name='API Tokens' active={pageName === PAGES.API_TOKENS} onClick={tabIt(PAGES.API_TOKENS)} />
            ) : null
          }
          <HelpMenuItem />
          <Menu.Item>
            signed in as { R.pathOr('unkown', ['displayname'], authdUser) }
          </Menu.Item>
          <Menu.Item>
            <Button primary onClick={_e => logout()}>Logout</Button>
          </Menu.Item>
        </Menu.Menu>
      </Menu>
      <Segment basic>
        <UsersPage display={pageName === PAGES.USERS} />
        <InstitutionsPage display={pageName === PAGES.INSTITUTIONS} />
        <ResellersPage display={pageName === PAGES.RESELLERS} />
        <SerialsPage display={pageName === PAGES.SERIALS} />
        <LicensesPage display={pageName === PAGES.LICENSES} />
        <FastspringPage display={pageName === PAGES.FASTSPRING} />
        <APITokensPage display={ pageName === PAGES.API_TOKENS } />
      </Segment>
    </Container>
  )
    : isInstAdmin(authdUser)
      ? (
        <Container fluid>
          <Menu pointing>
            <Menu.Menu position='left'>
              { authdUser.institutions.map(inst => (
                <Menu.Item
                  key={'inst_menu' + inst._id}
                  color={COLORS.INSTITUTIONS.MENU}
                  name={inst.name}
                  active={pageName === inst._id}
                  onClick={tabIt(inst._id)}
                />))
              }
            </Menu.Menu>
            <Menu.Menu position='right'>
              <HelpMenuItem />
              <Menu.Item>
                signed in as { R.pathOr('unkown', ['displayname'], authdUser) }
              </Menu.Item>
              <Menu.Item>
                <Button primary onClick={_e => logout()}>Logout</Button>
              </Menu.Item>
            </Menu.Menu>
          </Menu>
          <Segment>
            { authdUser.institutions.map(inst => (
              <ThirdPartyInstitution
                key={'inst_' + inst._id}
                display={pageName === inst._id}
                institutionId={inst._id}
              />
            ))
            }
          </Segment>
        </Container>
      )
      : (
        <Container fluid>
          <div style={{ padding: "64px 0", textAlign: "center" }}>
            <h3>Unauthorized</h3>
            <p>You do not have permission to view this page.</p>
            <Button primary onClick={logout}>Logout</Button>
          </div>
        </Container>
      )
}

_AuthMain.propTypes = {
  pageName: PropTypes.string,
  authdUser: PropTypes.object,
  navigation: PropTypes.object,
  loginError: PropTypes.string,

  loginAndGoToPage: PropTypes.func.isRequired,
  clearAuthError: PropTypes.func.isRequired,
  goToTab: PropTypes.func.isRequired,
  logout: PropTypes.func.isRequired,
  postLoginNavigation: PropTypes.string,
}

const getPageName = R.propOr(PAGES.USERS, 'pageName')
const getAuthdUser = R.pathOr(null, ['authStatus', 'user'])
const getLoginError = R.pathOr(null, ['authStatus', 'request', 'error'])
const getNavigation = state => R.path(['pages', R.propOr(PAGES.INSTITUTIONS, 'pageName', state), 'navigation'], state)
const getPostLoginNavigation = (state) => R.path(["authStatus", "navigatingTo"], state);

const mapStateToProps = state => ({
  pageName: getPageName(state),
  navigation: getNavigation(state),
  authdUser: getAuthdUser(state),
  loginError: getLoginError(state),
  postLoginNavigation: getPostLoginNavigation(state),
})

const mapDispatchToProps = dispatch => ({
  // institution actions
  loginAndGoToPage: R.compose(dispatch, loginAndGoToPage), // ({username, password, navigation}))
  clearAuthError: R.compose(dispatch, clearAuthError), // ()
  goToTab: R.compose(dispatch, goToTab), // ({pageName})
  logout: R.compose(dispatch, logout) // ()
})

const AuthMain = connect(
  mapStateToProps,
  mapDispatchToProps
)(_AuthMain)

const App = () => (
  <Provider store={store}>
    <UserProvider>
      <AuthMain />
    </UserProvider>
  </Provider>
)

export default App



// WEBPACK FOOTER //
// ./webAdmin/app.js